// store/index.js
import { createStore } from "vuex";
import {
  getFirestore, collection, getDocs, addDoc, doc, setDoc, updateDoc, deleteDoc, query, where
} from "firebase/firestore";
import { getAuth, signInWithPopup, GoogleAuthProvider, signOut } from "firebase/auth";
import { app } from "@/database/db.js"; // your Firebase v9 initialized app

const db = getFirestore(app);
const auth = getAuth(app);

export default createStore({
  state: {
    user: null,
    installations: [],
    people: [],
    sponsors: [],
    priceWinners: [],
    newsItems: [],
    admins: [],
  },
  getters: {
    getUser: (state) => state.user,
    getInstallations: (state) => state.installations,
    getPeople: (state) => state.people,
    getSponsors: (state) => state.sponsors,
    getPriceWinners: (state) => state.priceWinners,
    getNewsItems: (state) => state.newsItems,
    getAdmins: (state) => state.admins,
  },
  mutations: {
    SET_USER(state, user) { state.user = user; },
    SET_INSTALLATIONS(state, installations) { state.installations = installations; },
    SET_PEOPLE(state, people) { state.people = people; },
    SET_SPONSORS(state, sponsors) { state.sponsors = sponsors; },
    SET_PRICE_WINNERS(state, priceWinners) { state.priceWinners = priceWinners; },
    SET_NEWS_ITEMS(state, items) { state.newsItems = items; },
    ADD_NEWS_ITEM(state, item) {
      state.newsItems.push(item);
    },
    SET_ADMINS(state, admins) { state.admins = admins; },
  },
  actions: {
    /** Authentication **/
    async loginWithFirebase() {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
      const user = auth.currentUser;
      this.commit("SET_USER", user);
    },
    async logoutWithFirebase() {
      await signOut(auth);
      this.commit("SET_USER", null);
    },
    async getUser() {
      const user = auth.currentUser;
      this.commit("SET_USER", user || null);
    },

    /** Installations **/
    async getInstallations() {
      const colRef = collection(db, "installations");
      const snapshot = await getDocs(colRef);
      const installations = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      this.commit("SET_INSTALLATIONS", installations);
    },
    async addInstallation(_, payload) {

      try {
        if (!payload.id) {
          throw new Error("payload.id is required for custom document ID");
        }

        // Bruk payload.id som Firestore document ID// 2. Tving ID til string
        const docId = String(payload.id);
        const docRef = doc(db, 'installations', docId);

        // Fjern id fra dataene (valgfritt – unngå duplisering)
        await setDoc(docRef, { ...payload, id: docId });

        console.log('Installation added with ID:', payload.id);
      } catch (error) {
        console.error('addInstallation failed:', error);
        throw error;
      }
    },
    async updateInstallation(_, payload) {
      const colRef = collection(db, "installations");
      const q = query(colRef, where("id", "==", payload.id));
      const snapshot = await getDocs(q);
      snapshot.forEach(async (docSnap) => {
        const docRef = doc(db, "installations", docSnap.id);
        await updateDoc(docRef, { ...payload });
      });
    },
    async deleteInstallation(_, payload) {
      const id = payload.id;
      const colRef = collection(db, "installations");
      const q = query(colRef, where("id", "==", id));
      const snapshot = await getDocs(q);
      snapshot.forEach(async (docSnap) => {
        const docRef = doc(db, "installations", docSnap.id);
        await deleteDoc(docRef);
      });
    },

      async getPeople({ commit }) {
      const colRef = collection(db, "people");
      const snapshot = await getDocs(colRef);
      const people = snapshot.docs.map(doc => ({ docId: doc.id, ...doc.data() }));
      commit("SET_PEOPLE", people);
    },
    /** Add a new person */
    async addPerson({ commit }, payload) {
      // Save in Firestore
      const colRef = collection(db, "people");
      const docRef = await addDoc(colRef, payload);

      // Include Firestore doc ID
      const savedPerson = { docId: docRef.id, ...payload };
      commit("SET_PEOPLE", [...this.state.people, savedPerson]);
      return savedPerson;
    },
    /** Update an existing person */
    async updatePerson({ commit }, payload) {
      if (!payload.docId) throw new Error("Missing person document ID");

      const docRef = doc(db, "people", payload.docId);
      await setDoc(docRef, payload, { merge: true });

      // Update Vuex state
      const updatedPeople = this.state.people.map(p =>
        p.docId === payload.docId ? { ...p, ...payload } : p
      );
      commit("SET_PEOPLE", updatedPeople);
      return payload;
    },
    /** Delete a person */
    async deletePerson({ commit }, payload) {
      if (!payload.docId) throw new Error("Missing person document ID");

      const docRef = doc(db, "people", payload.docId);
      await deleteDoc(docRef);

      // Update Vuex state
      const remaining = this.state.people.filter(p => p.docId !== payload.docId);
      commit("SET_PEOPLE", remaining);
    },

    async getNewsItems({ commit }) {
      const colRef = collection(db, "newsItems");
      const snapshot = await getDocs(colRef);

      // Use the Firestore doc id as the identifier
      const items = snapshot.docs.map(doc => ({
        docId: doc.id, // store doc ID separately
        ...doc.data(),
      }));

      commit("SET_NEWS_ITEMS", items);
    },
    async addNewsItem({ commit }, payload) {
      const colRef = collection(db, "newsItems");
      const docRef = await addDoc(colRef, payload);

      // Return docId along with payload
      const savedItem = { docId: docRef.id, ...payload };
      commit("ADD_NEWS_ITEM", savedItem);
      return savedItem;
    },
    async updateNewsItem(_, payload) {
      if (!payload.docId) throw new Error("Missing Firestore document ID");

      const docRef = doc(db, "newsItems", payload.docId);
      const updateData = { ...payload };
      delete updateData.docId; // remove docId before updating Firestore

      await setDoc(docRef, updateData, { merge: true });
    }, 
    async deleteNewsItem(_, payload) {
      if (!payload.docId) throw new Error("Missing Firestore document ID");

      try {
        const docRef = doc(db, "newsItems", payload.docId);
        await deleteDoc(docRef);
        console.log(`News item deleted: ${payload.docId}`);
      } catch (err) {
        console.error("Failed to delete news item:", err);
        throw err;
      }
    },

    async getSponsors() {
      const colRef = collection(db, "sponsors");
      const snapshot = await getDocs(colRef);
      const sponsors = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      this.commit("SET_SPONSORS", sponsors);
    },
    async getPriceWinners() {
      const colRef = collection(db, "priceWinners");
      const snapshot = await getDocs(colRef);
      const winners = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      this.commit("SET_PRICE_WINNERS", winners);
    },

    async getAdmins() {
      const colRef = collection(db, "admins");
      const snapshot = await getDocs(colRef);
      const admins = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      this.commit("SET_ADMINS", admins);
    },
  },
  modules: {}
});